/* eslint-disable @typescript-eslint/naming-convention */
import { useAppSelector } from 'store/hooks';
import React from 'react';
import { Typography } from '@mui/material';
import { stringify } from 'query-string';
import { LocationSettingsApi } from 'components/location/LocationSettingsApi';
import { IPointOfSaleSettingsProps } from 'components/settings/PointOfSaleSettings';
import { SettingDisplay } from 'lib/SettingDisplay';
import { SettingDisplaySwitch } from 'components/settings/SettingDisplaySwitch';
import { SettingDisplayDictionary } from 'components/settings/SettingDisplayDictionary';
import { OrderScenarioLabels } from 'components/order/model/Order';
import { LoadingButton } from 'ui/buttons/LoadingButton';
import { OAuthProvider } from '@pepperhq/location-sdk';

const toScenarioEntries = (record?: Record<string, string>) =>
    Object.entries(record ?? {}).map(([key, value]) => ({
        label: OrderScenarioLabels[key] ?? key,
        value
    }));

export const LightspeedKSeriesSettingsDisplay: React.FC<IPointOfSaleSettingsProps> = ({
    settings,
    locationId,
    onSettingsSaved
}) => {
    const isAuthorized = React.useMemo(() => !!settings?.lightspeedKSeries?.oauth2AccessToken, [settings]);
    const accountProfileCodeEntries = React.useMemo(
        () => toScenarioEntries(settings?.lightspeedKSeries?.scenarioToAccountProfileCode),
        [settings]
    );
    const [loadingOAuth, setLoadingOAuth] = React.useState(false);
    const { tenantId } = useAppSelector(state => state.auth.tenant);

    const handleAuthorizeClicked = React.useCallback(async () => {
        setLoadingOAuth(true);
        if (isAuthorized) {
            await new LocationSettingsApi().revokeOAuthAccess(OAuthProvider.LIGHTSPEED_K_SERIES);
            if (typeof onSettingsSaved === 'function') {
                await onSettingsSaved();
            }
        } else {
            const authHost = import.meta.env.TIER === 'beta' ? 'auth.lsk-demo.app' : 'auth.lsk-prod.app';
            const query = {
                response_type: 'code',
                client_id: import.meta.env.LIGHTSPEED_K_SERIES_CLIENT_ID,
                redirect_uri: `${import.meta.env.MENU_SERVICE_URL}/oauth/lightspeedKSeries`,
                scope: 'orders-api offline_access',
                state: JSON.stringify({
                    tenantId,
                    successRedirectUrl: `${window.location.href}?oauthResult=success`,
                    errorRedirectUrl: `${window.location.href}?oauthResult=error`
                })
            };
            window.location.href = `https://${authHost}/realms/k-series/protocol/openid-connect/auth?${stringify(
                query
            )}`;
        }
        setLoadingOAuth(false);
    }, [isAuthorized, tenantId, onSettingsSaved]);

    return (
        <>
            {locationId === undefined && (
                <>
                    <SettingDisplay label="Host">
                        <Typography variant="body1">{settings?.lightspeedKSeries?.host}</Typography>
                    </SettingDisplay>
                    <SettingDisplay label="OAuth2">
                        <LoadingButton
                            variant={isAuthorized ? 'outlined' : 'contained'}
                            color="primary"
                            onClick={handleAuthorizeClicked}
                            loading={loadingOAuth}
                        >
                            {isAuthorized ? 'De-Authorize' : 'Authorize'}
                        </LoadingButton>
                    </SettingDisplay>
                    <SettingDisplaySwitch
                        title="Webhooks Enabled"
                        checked={settings?.lightspeedKSeries?.webhooksEnabled}
                    />
                </>
            )}
            {locationId !== undefined && (
                <>
                    <SettingDisplay label="Business Location ID">
                        <Typography variant="body1">{settings?.lightspeedKSeries?.locationId}</Typography>
                    </SettingDisplay>
                    <SettingDisplay label="Payment Method Code">
                        <Typography variant="body1">
                            {settings?.lightspeedKSeries?.paymentMethodCode}
                        </Typography>
                    </SettingDisplay>
                    <SettingDisplayDictionary
                        title="Account Profile Code Mapping"
                        entries={accountProfileCodeEntries}
                    />
                </>
            )}
        </>
    );
};
